import { useEffect, useState } from "react";
import { useOutletContext, useNavigate, useParams, Link } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const DEFAULT_FORM = {
    name: "",
    event_type: "concert",
    start_time: "20:00",
    end_time: "23:00",
    capacity: 100,
    description: "",
    venue_notes: "",
    is_free: true,
    is_pay_at_door: false,
    ticket_price: "",
    payment_instructions: "",
    external_payment_link: "",
};

export default function VenueTemplateEditor() {
    const { venue, role } = useOutletContext();
    const { tid } = useParams();
    const navigate = useNavigate();
    const isEdit = !!tid;
    const canEdit = role === "manager" || role === "admin";
    const [form, setForm] = useState(DEFAULT_FORM);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    useEffect(() => {
        if (!canEdit) { navigate(`/venue/${venue.id}/templates`); return; }
        if (!isEdit) return;
        endpoints.venueTemplate(venue.id, tid).then((d) => {
            const t = d.template;
            setForm({
                name: t.name || "",
                event_type: t.event_type || "concert",
                start_time: t.start_time || "20:00",
                end_time: t.end_time || "23:00",
                capacity: t.capacity || 100,
                description: t.description || "",
                venue_notes: t.venue_notes || "",
                is_free: !!t.is_free,
                is_pay_at_door: !!t.is_pay_at_door,
                ticket_price: t.ticket_price ?? "",
                payment_instructions: t.payment_instructions || "",
                external_payment_link: t.external_payment_link || "",
            });
        }).catch((e) => { toast.error(formatApiError(e)); navigate(`/venue/${venue.id}/templates`); })
        .finally(() => setLoading(false));
    }, [isEdit, tid, venue.id, canEdit, navigate]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.name.trim()) { toast.error("Template name is required"); return; }
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            event_type: form.event_type,
            start_time: form.start_time,
            end_time: form.end_time,
            capacity: parseInt(form.capacity || 100),
            description: form.description,
            venue_notes: form.venue_notes,
            is_free: form.is_free,
            is_pay_at_door: form.is_pay_at_door,
            ticket_price: form.is_free ? null : parseFloat(form.ticket_price || 0),
            payment_instructions: form.payment_instructions,
            external_payment_link: form.external_payment_link,
        };
        try {
            if (isEdit) {
                await endpoints.venueUpdateTemplate(venue.id, tid, payload);
                toast.success("Template updated");
            } else {
                await endpoints.venueCreateTemplate(venue.id, payload);
                toast.success("Template created");
            }
            navigate(`/venue/${venue.id}/templates`);
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    if (loading) return <div className="p-8 font-mono text-xs text-[#666]" data-testid="venue-template-editor-loading">Loading…</div>;

    return (
        <div className="pm-fade" data-testid="venue-template-editor">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <Link to={`/venue/${venue.id}/templates`} className="text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1 hover:text-[#ff4400]" data-testid="venue-template-back"><ArrowLeft className="w-3 h-3" /> Back to templates</Link>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mt-1">{isEdit ? "Edit template" : "New template"}</h1>
                    <p className="mt-1 text-xs text-[#a3a3a3] max-w-2xl">Editing a template does not change events already created from it.</p>
                </div>
                <button onClick={save} disabled={saving} className="pm-btn pm-btn-primary" data-testid="venue-template-save">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save template"}
                </button>
            </header>

            <div className="px-4 md:px-8 py-6 max-w-3xl space-y-4">
                <Field label="Template name">
                    <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Sunday jazz night" className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-name" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Event type">
                        <select value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono text-sm" data-testid="venue-template-type">
                            <option value="concert" className="bg-[#141414]">Concert</option>
                            <option value="dj_set" className="bg-[#141414]">DJ set</option>
                            <option value="open_mic" className="bg-[#141414]">Open mic</option>
                            <option value="listening_party" className="bg-[#141414]">Listening party</option>
                            <option value="showcase" className="bg-[#141414]">Showcase</option>
                            <option value="community" className="bg-[#141414]">Community night</option>
                        </select>
                    </Field>
                    <Field label="Start time">
                        <input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-start" />
                    </Field>
                    <Field label="End time">
                        <input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-end" />
                    </Field>
                </div>
                <Field label="Capacity">
                    <input type="number" min="1" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-capacity" />
                </Field>
                <Field label="Default description">
                    <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-desc" />
                </Field>
                <Field label="Venue notes (internal)">
                    <textarea rows={2} value={form.venue_notes} onChange={(e) => set("venue_notes", e.target.value)} placeholder="Load-in, backline, sound checks…" className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-notes" />
                </Field>

                <div className="border border-[#2a2a2a] p-4 space-y-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Access mode</div>
                    <div className="flex flex-wrap gap-2">
                        <label className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 cursor-pointer">
                            <input type="checkbox" checked={form.is_free} onChange={(e) => set("is_free", e.target.checked)} className="accent-[#ff4400]" data-testid="venue-template-free" />
                            <span className="text-xs font-mono uppercase">Free entry</span>
                        </label>
                        {!form.is_free && (
                            <label className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 cursor-pointer">
                                <input type="checkbox" checked={form.is_pay_at_door} onChange={(e) => set("is_pay_at_door", e.target.checked)} className="accent-[#ff4400]" data-testid="venue-template-door" />
                                <span className="text-xs font-mono uppercase">Pay at door</span>
                            </label>
                        )}
                    </div>
                    {!form.is_free && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Display ticket price (USD)">
                                <input type="number" step="0.01" value={form.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-price" />
                            </Field>
                            <Field label="External payment link (optional)">
                                <input value={form.external_payment_link} onChange={(e) => set("external_payment_link", e.target.value)} placeholder="https://…" className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-payment-link" />
                            </Field>
                        </div>
                    )}
                    {form.is_pay_at_door && (
                        <Field label="Pay-at-door instructions">
                            <input value={form.payment_instructions} onChange={(e) => set("payment_instructions", e.target.value)} placeholder="Cash only at the door. Card via Revolut accepted." className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-template-payment-notes" />
                        </Field>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">{label}</div>
            {children}
        </label>
    );
}
